import { useCallback, useEffect, useRef, useState } from 'react';
import { registerSW } from 'virtual:pwa-register';
import { hasActionableWaitingServiceWorker } from './pwaUpdate';

const DISMISS_KEY = 'pwa_install_dismissed';
const DISMISS_DAYS = 30;
const UPDATE_CHECK_INTERVAL_MS = 15 * 60 * 1000;

function isDismissedRecently(): boolean {
  try {
    const ts = localStorage.getItem(DISMISS_KEY);
    if (!ts) return false;
    const diff = Date.now() - Number(ts);
    return diff < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

function detectMobile(): boolean {
  return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

function detectIOS(): boolean {
  const standaloneNavigator = navigator as Navigator & { standalone?: boolean };
  return /iPhone|iPad|iPod/i.test(navigator.userAgent) && !standaloneNavigator.standalone;
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export function usePWA() {
  const [needRefresh, setNeedRefresh] = useState(false);
  const [offlineReady, setOfflineReady] = useState(false);
  const [isApplyingUpdate, setIsApplyingUpdate] = useState(false);
  const registrationRef = useRef<ServiceWorkerRegistration | undefined>();
  const updateSWRef = useRef<((reloadPage?: boolean) => Promise<void>) | undefined>();

  // ── Install prompt state ──────────────────────────────────────────
  const deferredPrompt = useRef<BeforeInstallPromptEvent | null>(null);
  const [canInstall, setCanInstall] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isDismissed, setIsDismissed] = useState(isDismissedRecently);
  const isMobile = detectMobile();
  const isIOS = detectIOS();

  const validatePendingUpdate = useCallback(async () => {
    if (!('serviceWorker' in navigator)) {
      setNeedRefresh(false);
      return false;
    }

    const registration =
      registrationRef.current ??
      (await navigator.serviceWorker.getRegistration().catch(() => undefined));
    registrationRef.current = registration;

    const hasWaitingUpdate = hasActionableWaitingServiceWorker(
      registration,
      navigator.serviceWorker.controller,
    );

    setNeedRefresh(hasWaitingUpdate);
    return hasWaitingUpdate;
  }, []);

  useEffect(() => {
    // Android / Chrome: capture the browser's native install prompt
    const handler = (e: Event) => {
      e.preventDefault();
      deferredPrompt.current = e as BeforeInstallPromptEvent;
      setCanInstall(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // Hide prompt once app is installed
    const installedHandler = () => {
      setIsInstalled(true);
      setCanInstall(false);
    };
    window.addEventListener('appinstalled', installedHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    try {
      let lastUpdateCheck = 0;
      let intervalId: ReturnType<typeof setInterval> | undefined;
      let registeredUpdateFoundHandler:
        | { registration: ServiceWorkerRegistration; handler: () => void }
        | undefined;
      const checkForUpdate = () => {
        if (!registrationRef.current) return;
        const now = Date.now();
        if (now - lastUpdateCheck < 30_000) return;
        lastUpdateCheck = now;
        registrationRef.current.update().then(() => {
          void validatePendingUpdate();
        }).catch(() => {
          // Non-critical: field workflows must never break because SW update
          // probing failed on a weak connection.
        });
      };
      const update = registerSW({
        onNeedRefresh() { void validatePendingUpdate(); },
        onOfflineReady() { setOfflineReady(true); },
        onRegisteredSW(_swUrl: string, registration: ServiceWorkerRegistration | undefined) {
          if (registration) {
            registrationRef.current = registration;
            checkForUpdate();
            intervalId = setInterval(checkForUpdate, UPDATE_CHECK_INTERVAL_MS);
            const onUpdateFound = () => {
              const installingWorker = registration.installing;
              installingWorker?.addEventListener('statechange', () => {
                if (installingWorker.state === 'installed') {
                  void validatePendingUpdate();
                }
              });
            };
            registration.addEventListener('updatefound', onUpdateFound);
            registeredUpdateFoundHandler = { registration, handler: onUpdateFound };
          }
        },
        onRegisterError(error: Error) {
          // Silently log — never let SW errors surface as unhandled rejections
          console.warn('SW registration error (non-critical):', error);
        },
      });
      updateSWRef.current = update;
      void validatePendingUpdate();
      const onFocus = () => checkForUpdate();
      const onOnline = () => checkForUpdate();
      const onVisibility = () => {
        if (document.visibilityState === 'visible') checkForUpdate();
      };
      const onControllerChange = () => {
        setNeedRefresh(false);
        setOfflineReady(false);
      };
      window.addEventListener('focus', onFocus);
      window.addEventListener('online', onOnline);
      document.addEventListener('visibilitychange', onVisibility);
      navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
      return () => {
        window.removeEventListener('focus', onFocus);
        window.removeEventListener('online', onOnline);
        document.removeEventListener('visibilitychange', onVisibility);
        navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
        if (registeredUpdateFoundHandler) {
          registeredUpdateFoundHandler.registration.removeEventListener(
            'updatefound',
            registeredUpdateFoundHandler.handler,
          );
        }
        if (intervalId) clearInterval(intervalId);
      };
    } catch (err) {
      console.warn('SW setup failed (non-critical):', err);
    }
    return undefined;
  }, [validatePendingUpdate]);

  const close = () => {
    setNeedRefresh(false);
    setOfflineReady(false);
  };

  const applyUpdate = async () => {
    setIsApplyingUpdate(true);
    const hasWaitingUpdate = await validatePendingUpdate();

    if (!hasWaitingUpdate) {
      setIsApplyingUpdate(false);
      return;
    }

    try {
      if (updateSWRef.current) {
        await updateSWRef.current(true);
        return;
      }

      registrationRef.current?.waiting?.postMessage({ type: 'SKIP_WAITING' });
      window.setTimeout(() => window.location.reload(), 800);
    } finally {
      setIsApplyingUpdate(false);
    }
  };

  const triggerInstall = async () => {
    if (!deferredPrompt.current) return;
    await deferredPrompt.current.prompt();
    const { outcome } = await deferredPrompt.current.userChoice;
    deferredPrompt.current = null;
    setCanInstall(false);
    if (outcome === 'dismissed') dismissInstall();
  };

  const dismissInstall = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      // Safari private mode can block storage; dismissal still works in-memory.
    }
    setIsDismissed(true);
  };

  // Show the install sheet if: mobile, not already installed, not dismissed,
  // and either the browser has a deferred prompt (Android) or it's iOS Safari
  const showInstallPrompt = isMobile && !isInstalled && !isDismissed && (canInstall || isIOS);

  return {
    needRefresh,
    offlineReady,
    updateSW: applyUpdate,
    applyUpdate,
    isApplyingUpdate,
    close,
    canInstall,
    isIOS,
    isMobile,
    showInstallPrompt,
    triggerInstall,
    dismissInstall,
  };
}
